// The Redacamy mobile app opens SPP inside a web view with `embedded=true` and the learner's Keycloak access
// token (`token=...`), either in the query string or after the `#`. Both are read as soon as this module loads
// (before any login redirect), the token is removed from the address bar straight away, and the embedded flag
// is kept for the tab's session so every later page knows it runs inside the app.
const STORAGE_KEY = "spiritual-profile-platform-embedded";
const TRUE_VALUES = new Set(["1", "true", "yes"]);

let memoryFlag = false;

const parseParams = (raw) => {
  try {
    return new URLSearchParams(String(raw || "").replace(/^[?#]/, ""));
  } catch {
    return new URLSearchParams();
  }
};

// Pure helper (unit tested): what the launch URL asks for.
export const extractLaunchParams = (search, hash) => {
  const fromSearch = parseParams(search);
  const fromHash = parseParams(hash);
  const embeddedRaw = fromSearch.get("embedded") ?? fromHash.get("embedded");
  const token = (fromSearch.get("token") ?? fromHash.get("token") ?? "").trim();
  return {
    embedded: embeddedRaw === null ? null : TRUE_VALUES.has(String(embeddedRaw).toLowerCase()),
    token: token || null,
  };
};

// Pure helper (unit tested): the same URL without the token, so it never stays in the address bar or history.
export const withoutLaunchToken = (pathname, search, hash) => {
  const query = parseParams(search);
  query.delete("token");
  const fragment = parseParams(hash);
  fragment.delete("token");
  const q = query.toString();
  const h = fragment.toString();
  return `${pathname}${q ? `?${q}` : ""}${h ? `#${h}` : ""}`;
};

const remember = (value) => {
  memoryFlag = value;
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, "1");
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // The in-memory flag still covers this page load.
  }
};

export const isEmbedded = () => {
  if (memoryFlag) return true;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const capture = () => {
  if (typeof window === "undefined") return null;
  const { pathname, search, hash } = window.location;
  const { embedded, token } = extractLaunchParams(search, hash);

  if (embedded !== null) remember(embedded);
  if (isEmbedded()) document.documentElement.dataset.embedded = "true";
  else delete document.documentElement.dataset.embedded;

  if (token) {
    // A token in the address is removed whether or not it is used, so it cannot leak through history or logs.
    try {
      window.history.replaceState(window.history.state, "", withoutLaunchToken(pathname, search, hash));
    } catch {
      // Not fatal: the token is still only used once below.
    }
  }

  // Only the mobile app may hand over a token; a stray ?token= in a normal browser is ignored.
  return token && isEmbedded() ? token : null;
};

// Captured once at load so a repeated effect run (React strict mode) still sees it.
export const launchToken = capture();
